import { type ChangeEvent, type KeyboardEvent, useRef } from 'react'

import { clampProficiencyRange } from '@/lib/recommendation/proficiency-range'
import { cn } from '@/lib/utils'

export interface ProficiencyRangeSliderProps {
  min: number
  max: number
  currentMastery: number
  onMinChange: (value: number) => void
  onMaxChange: (value: number) => void
  onMinCommit?: (value: number) => void
  onMaxCommit?: (value: number) => void
  disabled?: boolean
  hideCurrentMarker?: boolean
  className?: string
  'aria-label'?: string
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value))
}

export function ProficiencyRangeSlider({
  min,
  max,
  currentMastery,
  onMinChange,
  onMaxChange,
  onMinCommit,
  onMaxCommit,
  disabled = false,
  hideCurrentMarker = false,
  className,
  'aria-label': ariaLabel,
}: ProficiencyRangeSliderProps) {
  const pendingMinCommitRef = useRef<number>(min)
  const pendingMaxCommitRef = useRef<number>(max)
  const maxAdjustedDuringMinDragRef = useRef(false)
  const minAdjustedDuringMaxDragRef = useRef(false)

  const minPct = clampPercent(min)
  const maxPct = clampPercent(max)
  const currentPct = clampPercent(currentMastery)
  const fillWidth = Math.max(0, maxPct - minPct)
  /** Split pointer hit zones so the stacked range inputs do not block each other. */
  const hitSplitPct = (minPct + maxPct) / 2

  const minInputLabel = ariaLabel
    ? `${ariaLabel} minimum`
    : 'minimum proficiency'
  const maxInputLabel = ariaLabel
    ? `${ariaLabel} maximum`
    : 'maximum proficiency'

  const handleMinChange = (nextMin: number) => {
    const clamped = clampProficiencyRange(nextMin, max)
    pendingMinCommitRef.current = clamped.minProficiencyThreshold
    pendingMaxCommitRef.current = clamped.maxProficiencyThreshold
    maxAdjustedDuringMinDragRef.current =
      clamped.maxProficiencyThreshold !== max
    onMinChange(clamped.minProficiencyThreshold)
    if (maxAdjustedDuringMinDragRef.current) {
      onMaxChange(clamped.maxProficiencyThreshold)
    }
  }

  const handleMaxChange = (nextMax: number) => {
    const clamped = clampProficiencyRange(min, nextMax)
    pendingMinCommitRef.current = clamped.minProficiencyThreshold
    pendingMaxCommitRef.current = clamped.maxProficiencyThreshold
    minAdjustedDuringMaxDragRef.current =
      clamped.minProficiencyThreshold !== min
    onMaxChange(clamped.maxProficiencyThreshold)
    if (minAdjustedDuringMaxDragRef.current) {
      onMinChange(clamped.minProficiencyThreshold)
    }
  }

  const commitMinBounds = () => {
    onMinCommit?.(pendingMinCommitRef.current)
    if (maxAdjustedDuringMinDragRef.current) {
      onMaxCommit?.(pendingMaxCommitRef.current)
      maxAdjustedDuringMinDragRef.current = false
    }
  }

  const commitMaxBounds = () => {
    onMaxCommit?.(pendingMaxCommitRef.current)
    if (minAdjustedDuringMaxDragRef.current) {
      onMinCommit?.(pendingMinCommitRef.current)
      minAdjustedDuringMaxDragRef.current = false
    }
  }

  const handleCommitKey =
    (commit: () => void) => (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        commit()
      }
    }

  return (
    <div className={cn('relative w-full pt-7', className)}>
      <div className="relative flex h-5 w-full items-center">
        <div
          className={cn(
            'h-2 w-full rounded-full bg-muted',
            disabled && 'bg-muted/60',
          )}
        />
        <div
          data-testid="proficiency-range-fill"
          className={cn(
            'absolute h-2 rounded-full bg-primary/60',
            disabled && 'bg-muted-foreground/30',
          )}
          style={{ left: `${minPct}%`, width: `${fillWidth}%` }}
        />

        <div
          className="absolute inset-0 z-20"
          style={{ clipPath: `inset(0 ${100 - hitSplitPct}% 0 0)` }}
        >
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={minPct}
            disabled={disabled}
            aria-label={minInputLabel}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleMinChange(Number(e.target.value))
            }
            onPointerUp={commitMinBounds}
            onBlur={commitMinBounds}
            onKeyUp={handleCommitKey(commitMinBounds)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          />
        </div>
        <div
          className="absolute inset-0 z-30"
          style={{ clipPath: `inset(0 0 0 ${hitSplitPct}%)` }}
        >
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={maxPct}
            disabled={disabled}
            aria-label={maxInputLabel}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleMaxChange(Number(e.target.value))
            }
            onPointerUp={commitMaxBounds}
            onBlur={commitMaxBounds}
            onKeyUp={handleCommitKey(commitMaxBounds)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          />
        </div>

        <div
          className={cn(
            'border-background pointer-events-none absolute z-40 h-4 w-4 rounded-full border-2 bg-primary shadow',
            disabled && 'bg-muted-foreground/50',
          )}
          style={{ left: `calc(${minPct}% - 0.5rem)` }}
        />
        <div
          className={cn(
            'border-background pointer-events-none absolute z-40 h-4 w-4 rounded-full border-2 bg-primary shadow',
            disabled && 'bg-muted-foreground/50',
          )}
          style={{ left: `calc(${maxPct}% - 0.5rem)` }}
        />
      </div>

      {!hideCurrentMarker && (
        <div
          data-testid="proficiency-current-marker"
          className={cn(
            'pointer-events-none absolute top-0 -translate-x-1/2 text-xs font-medium text-foreground',
            disabled && 'text-muted-foreground',
          )}
          style={{ left: `${currentPct}%` }}
        >
          <span className="mb-1 block text-center">{currentMastery}</span>
          <span
            className={cn(
              'mx-auto block h-3 w-px bg-foreground/70',
              disabled && 'bg-muted-foreground',
            )}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  )
}
